import { VueQueryPlugin } from '@tanstack/vue-query'
import { createPinia } from 'pinia'
import { createApp } from 'vue'

import { setRequestLocale } from '@/api/http'
import { createQueryClient } from '@/api/query'
import { toast } from '@/shared/composables/useToast'
import { installKit } from '@/shared/ui/install'

import { vCan } from './acl'
import App from './App.vue'
import { ConfigError, loadConfig, type AppConfig } from './config'
import {
  availableLocales,
  createAppI18n,
  currentLocale,
  DEFAULT_LOCALE,
  getI18n,
  installCatalogueLoader,
  setLocale,
} from './i18n'
import { browserLocale, navigatorLanguages } from './i18n/browser-locale'
import { installAnalytics } from './integrations/analytics'
import { installMediaLinks } from './media-links'
import { loadCrisp } from './integrations/crisp'
import { createAppRouter, installGuards } from './router'
import { installSession, syncSoftwareWithRoute } from './session'
import { installUnauthorizedHandler } from './session/unauthorized'
import { useAuthStore } from './stores/auth'
import { installTheme } from './theme'

function renderBootError(error: unknown): void {
  const target = document.getElementById('app')
  const detail = error instanceof ConfigError ? error.issues.join('\n') : String(error)
  if (target) {
    const pre = document.createElement('pre')
    pre.style.cssText = 'padding:2rem;white-space:pre-wrap;font-family:monospace'
    pre.textContent = `Skileo could not start.\n\n${detail}`
    target.replaceChildren(pre)
  }
  console.error(error)
}

/**
 * Boot sequence (PLAN.md item 0.5, wp/shell §4.10): config → theme and kit → i18n `en` → session restore → stored
 * locale (OPEN-6 (a): before the first paint, `en` on failure — no blank page, NEW-8) → router with the guards, the
 * 401 handler and the software sync → mount. Nothing renders before the router has resolved the first navigation.
 */
async function boot(): Promise<void> {
  let config: AppConfig
  try {
    config = loadConfig()
  } catch (error) {
    renderBootError(error)
    return
  }

  const app = createApp(App)
  installTheme(app)
  installKit(app)
  const pinia = createPinia()
  app.use(pinia)

  const i18n = createAppI18n({ placeholderUntranslated: config.usePlaceholderUntranslated })
  app.use(i18n)

  const auth = useAuthStore(pinia)
  auth.restore()
  // The kept language, else (a first visit) the browser's when the app has it (i18n/browser-locale.ts).
  const locale = auth.prefs.locale ?? browserLocale(navigatorLanguages(), availableLocales())
  if (locale && locale !== DEFAULT_LOCALE) await setLocale(locale)
  // Owner default (wp/shell NOTES): `lang` on request bodies is the active UI locale, not read from the URL (NEW-5).
  setRequestLocale(() => currentLocale())

  const router = createAppRouter()
  const queryClient = createQueryClient()
  installSession({ router, queryClient })
  installUnauthorizedHandler(router)
  installGuards(router, {
    session: () => auth.session,
    maintenanceMode: () => config.maintenanceMode,
    onWizardRedirect: () => toast.danger(getI18n().global.t('shell.wizard.toast.completeProfile')),
  })
  router.afterEach((to) => syncSoftwareWithRoute(to))
  installCatalogueLoader(router)
  app.use(router)

  app.use(VueQueryPlugin, { queryClient })
  app.directive('can', vCan)

  loadCrisp(config.crispWebsiteId)
  installAnalytics(router, config.gaMeasurementId)
  // File links expire after 5 minutes (API S-55): an expired one gets a fresh link where it is clicked or shown.
  installMediaLinks()

  await router.isReady()
  app.mount('#app')
}

void boot()
